import {
  clamp,
  rectCenter,
  vDist,
  vRotate,
  type ImageNode,
  type Rect,
  type ResizeHandle,
  type Vec,
} from '@kanvas/board-core';
import { invalidate, scratch } from '../../state/scratch';
import { useEditor } from '../../state/store';
import type { Gesture, GestureContext } from '../types';

const MIN_CROP = 0.05;
const SAMPLE_SPACING = 2;

function fullCrop(): Rect {
  return { x: 0, y: 0, w: 1, h: 1 };
}

/** Normalised crop currently being edited, or the image's saved crop. */
export function activeCrop(node: ImageNode): Rect {
  return scratch.cropDraft ?? node.crop ?? fullCrop();
}

/** Freehand mask draft, or the image's saved mask. */
export function activeMask(node: ImageNode): Vec[] | null {
  if (scratch.cropMaskDraft && scratch.cropMaskDraft.length >= 3) return scratch.cropMaskDraft;
  return node.mask ?? null;
}

/**
 * Pointer position in the image's own, un-rotated frame.
 *
 * The crop window, its handles and the lasso all live in that frame (the
 * image is painted rotated about its box centre), so a rotated photo is
 * cropped by first spinning the pointer back the other way.
 */
export function toImageLocal(node: ImageNode, p: Vec): Vec {
  if (node.rotation === 0) return p;
  return vRotate(p, rectCenter({ x: node.x, y: node.y, w: node.w, h: node.h }), -node.rotation);
}

/**
 * Where a resized box must sit so that it stays put on screen after a crop.
 *
 * Nodes rotate about their own centre, and cropping moves that centre; without
 * this correction the visible pixels jump the moment the crop is applied.
 */
function keepRotatedBox(node: ImageNode, next: Rect): Rect {
  if (node.rotation === 0) return next;
  const pivot = rectCenter({ x: node.x, y: node.y, w: node.w, h: node.h });
  const own = rectCenter(next);
  const spun = vRotate(own, pivot, node.rotation);
  return { ...next, x: next.x + (spun.x - own.x), y: next.y + (spun.y - own.y) };
}

export function cropSourceBox(node: ImageNode, crop: Rect): Rect {
  const scaleX = node.w / Math.max(crop.w * node.naturalW, 1e-6);
  const scaleY = node.h / Math.max(crop.h * node.naturalH, 1e-6);
  return {
    x: node.x - crop.x * node.naturalW * scaleX,
    y: node.y - crop.y * node.naturalH * scaleY,
    w: node.naturalW * scaleX,
    h: node.naturalH * scaleY,
  };
}

/**
 * The full image laid out on the board while cropping.
 *
 * It is derived from the *saved* crop — the one `node.x/y/w/h` describe — and
 * never from the draft: the draft only moves the crop window inside it.
 * Deriving it from the draft made the window stay put under a handle while
 * the photo grew instead, and the committed crop didn't match the preview.
 */
export function savedCropSource(node: ImageNode): Rect {
  return cropSourceBox(node, node.crop ?? fullCrop());
}

export function beginImageCrop(nodeId: string): void {
  const editor = useEditor.getState();
  editor.setCropping(nodeId);
  scratch.cropDraft = null;
  scratch.cropPath = null;
  scratch.cropMaskDraft = null;
  invalidate();
}

export function cancelImageCrop(): void {
  useEditor.getState().setCropping(null);
  scratch.cropDraft = null;
  scratch.cropPath = null;
  scratch.cropMaskDraft = null;
  invalidate();
}

export function applyImageCrop(ctx: { doc: GestureContext['doc'] }): void {
  const id = useEditor.getState().croppingId;
  if (!id) return;
  const node = ctx.doc.get(id);
  if (!node || node.type !== 'image') {
    cancelImageCrop();
    return;
  }

  const crop = scratch.cropDraft ?? node.crop ?? fullCrop();
  const prev = node.crop ?? fullCrop();
  const mask =
    scratch.cropMaskDraft && scratch.cropMaskDraft.length >= 3
      ? scratch.cropMaskDraft
      : node.mask;

  const scaleX = node.w / Math.max(prev.w * node.naturalW, 1e-6);
  const scaleY = node.h / Math.max(prev.h * node.naturalH, 1e-6);
  const nextW = Math.max(crop.w * node.naturalW * scaleX, 8);
  const nextH = Math.max(crop.h * node.naturalH * scaleY, 8);
  const nextX = node.x + (crop.x - prev.x) * node.naturalW * scaleX;
  const nextY = node.y + (crop.y - prev.y) * node.naturalH * scaleY;

  const isFull =
    crop.w >= 1 - 1e-6 &&
    crop.h >= 1 - 1e-6 &&
    crop.x <= 1e-6 &&
    crop.y <= 1e-6 &&
    !(mask && mask.length >= 3);

  const box = keepRotatedBox(node, { x: nextX, y: nextY, w: nextW, h: nextH });
  ctx.doc.update<ImageNode>(id, {
    crop: isFull ? null : crop,
    mask: isFull ? null : mask && mask.length >= 3 ? mask : null,
    x: box.x,
    y: box.y,
    w: box.w,
    h: box.h,
  });
  ctx.doc.breakUndoBatch();
  cancelImageCrop();
}

export function resetImageCrop(ctx: { doc: GestureContext['doc'] }, nodeId: string): void {
  const node = ctx.doc.get(nodeId);
  if (!node || node.type !== 'image') return;
  if (!node.crop && !node.mask) return;
  const crop = node.crop ?? fullCrop();
  const box = keepRotatedBox(node, cropSourceBox(node, crop));
  ctx.doc.update<ImageNode>(nodeId, {
    crop: null,
    mask: null,
    x: box.x,
    y: box.y,
    w: box.w,
    h: box.h,
  });
  ctx.doc.breakUndoBatch();
  invalidate();
}

function clampCrop(crop: Rect): Rect {
  const w = clamp(crop.w, MIN_CROP, 1);
  const h = clamp(crop.h, MIN_CROP, 1);
  const x = clamp(crop.x, 0, 1 - w);
  const y = clamp(crop.y, 0, 1 - h);
  return { x, y, w, h };
}

function boardToNatural(p: Vec, source: Rect): Vec {
  return {
    x: clamp((p.x - source.x) / Math.max(source.w, 1e-6), 0, 1),
    y: clamp((p.y - source.y) / Math.max(source.h, 1e-6), 0, 1),
  };
}

function boundsOfPoints(points: readonly Vec[]): Rect {
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const p of points) {
    if (p.x < minX) minX = p.x;
    if (p.y < minY) minY = p.y;
    if (p.x > maxX) maxX = p.x;
    if (p.y > maxY) maxY = p.y;
  }
  if (!Number.isFinite(minX)) return fullCrop();
  return clampCrop({
    x: minX,
    y: minY,
    w: Math.max(maxX - minX, MIN_CROP),
    h: Math.max(maxY - minY, MIN_CROP),
  });
}

/**
 * Drag a crop handle. While cropping, the image paints its full source into a
 * temporary "source box" derived from the current crop, and the handles move
 * the normalised crop rectangle inside that box.
 */
export function createCropGesture(
  ctx: GestureContext,
  handle: ResizeHandle,
  node: ImageNode,
): Gesture {
  const start = activeCrop(node);
  const origin = toImageLocal(node, ctx.pointer.board);
  const source = savedCropSource(node);

  scratch.cropDraft = { ...start };
  scratch.gestureActive = true;

  const applyDelta = (at: Vec) => {
    const board = toImageLocal(node, at);
    const dx = (board.x - origin.x) / source.w;
    const dy = (board.y - origin.y) / source.h;
    let { x, y, w, h } = start;

    if (handle.includes('w')) {
      const nextX = clamp(start.x + dx, 0, start.x + start.w - MIN_CROP);
      w = start.w + (start.x - nextX);
      x = nextX;
    }
    if (handle.includes('e')) {
      w = clamp(start.w + dx, MIN_CROP, 1 - start.x);
    }
    if (handle.includes('n')) {
      const nextY = clamp(start.y + dy, 0, start.y + start.h - MIN_CROP);
      h = start.h + (start.y - nextY);
      y = nextY;
    }
    if (handle.includes('s')) {
      h = clamp(start.h + dy, MIN_CROP, 1 - start.y);
    }

    scratch.cropDraft = clampCrop({ x, y, w, h });
    invalidate();
  };

  return {
    cursor: 'default',
    move(next) {
      applyDelta(next.pointer.board);
    },
    up(next) {
      applyDelta(next.pointer.board);
      scratch.gestureActive = false;
      invalidate();
    },
    cancel() {
      scratch.cropDraft = { ...start };
      scratch.gestureActive = false;
      invalidate();
    },
  };
}

/** Drag the crop window itself (move, not resize). */
export function createCropMoveGesture(ctx: GestureContext, node: ImageNode): Gesture {
  const start = activeCrop(node);
  const origin = toImageLocal(node, ctx.pointer.board);
  const { w: sourceW, h: sourceH } = savedCropSource(node);

  scratch.cropDraft = { ...start };
  scratch.gestureActive = true;

  return {
    cursor: 'move',
    move(next) {
      const local = toImageLocal(node, next.pointer.board);
      const dx = (local.x - origin.x) / sourceW;
      const dy = (local.y - origin.y) / sourceH;
      scratch.cropDraft = clampCrop({
        x: start.x + dx,
        y: start.y + dy,
        w: start.w,
        h: start.h,
      });
      invalidate();
    },
    up() {
      scratch.gestureActive = false;
      invalidate();
    },
    cancel() {
      scratch.cropDraft = { ...start };
      scratch.gestureActive = false;
      invalidate();
    },
  };
}

/**
 * Freehand lasso crop: draw over the image to keep only that region.
 * The stroke's bounding box becomes the rectangular crop; the path is the mask.
 */
export function createCropDrawGesture(ctx: GestureContext, node: ImageNode): Gesture {
  // Draw against the full source so the user can reach any part of the photo.
  const source = savedCropSource(node);
  const zoom = useEditor.getState().viewport.zoom;
  const spacing = SAMPLE_SPACING / Math.max(zoom, 0.05);

  // Sampled in the image's own frame; the overlay paints them rotated back.
  const boardPoints: Vec[] = [toImageLocal(node, ctx.pointer.board)];
  scratch.cropPath = boardPoints;
  scratch.gestureActive = true;
  invalidate();

  return {
    cursor: 'crosshair',
    coalesce: true,
    move(next) {
      const last = boardPoints[boardPoints.length - 1]!;
      const local = toImageLocal(node, next.pointer.board);
      if (vDist(last, local) < spacing) return;
      boardPoints.push(local);
      scratch.cropPath = boardPoints;
      invalidate();
    },
    up() {
      scratch.gestureActive = false;
      scratch.cropPath = null;

      if (boardPoints.length < 3) {
        invalidate();
        return;
      }

      // Close the loop if the user almost returned to the start.
      const first = boardPoints[0]!;
      const last = boardPoints[boardPoints.length - 1]!;
      if (vDist(first, last) > 12 / zoom) {
        boardPoints.push({ ...first });
      }

      const natural = boardPoints.map((p) => boardToNatural(p, source));
      const box = boundsOfPoints(natural);
      scratch.cropDraft = box;
      scratch.cropMaskDraft = natural;
      invalidate();
    },
    cancel() {
      scratch.cropPath = null;
      scratch.gestureActive = false;
      invalidate();
    },
  };
}
